/**
 * F-148 AC9: pack lines show "3 packs × 12 = 36 units" and need an explicit confirmation of the
 * units-per-pack conversion. Any change to packs or units-per-pack clears the confirmation
 * (handled by the shared `applyQuantityEdit`).
 */
import { useId } from 'react';
import { useTranslation } from '@dukanti/shared';
import { RECEIVING_LIMITS, type ReceivingLineIssue } from '@dukanti/shared/admin';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { issueMessageKey } from '../lib/error-messages';
import { QuantityInput } from './line-quantity-grid';

export interface PackConversionFieldProps {
  readonly packsExpected: number | null;
  readonly unitsPerPack: number | null;
  readonly unitsPerPackConfirmed: number | null;
  readonly issues: readonly ReceivingLineIssue[];
  readonly disabled?: boolean;
  readonly onEdit: (field: 'packsExpected' | 'unitsPerPack', value: number | null) => void;
  readonly onConfirm: (confirmed: boolean) => void;
  readonly className?: string;
}

export function PackConversionField({
  packsExpected,
  unitsPerPack,
  unitsPerPackConfirmed,
  issues,
  disabled = false,
  onEdit,
  onConfirm,
  className,
}: PackConversionFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const packsId = `${id}-packs`;
  const perPackId = `${id}-per-pack`;
  const confirmId = `${id}-confirm`;

  const total =
    packsExpected !== null && unitsPerPack !== null && packsExpected > 0 && unitsPerPack > 0
      ? packsExpected * unitsPerPack
      : null;
  const confirmed = unitsPerPack !== null && unitsPerPackConfirmed === unitsPerPack;
  const issueFor = (field: string): ReceivingLineIssue | undefined =>
    issues.find((issue) => issue.field === field);
  const packsIssue = issueFor('packsExpected');
  const perPackIssue = issueFor('unitsPerPack');
  const confirmIssue = issueFor('unitsPerPackConfirmed');

  return (
    <fieldset className={cn('space-y-3 text-start', className)} data-testid="pack-conversion-field">
      <legend className="sr-only">{t('admin.receiving.line.pack')}</legend>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor={packsId} className="text-xs">
            {t('admin.receiving.line.packs')}
          </Label>
          <QuantityInput
            id={packsId}
            value={packsExpected}
            onChange={(value) => onEdit('packsExpected', value)}
            disabled={disabled}
            invalid={Boolean(packsIssue)}
            describedBy={packsIssue ? `${packsId}-error` : undefined}
            testId="pack-packs-input"
          />
          {packsIssue ? (
            <p id={`${packsId}-error`} className="text-destructive text-xs">
              {t(issueMessageKey(packsIssue.code), {
                ...packsIssue.meta,
                max: RECEIVING_LIMITS.maxPacks,
              })}
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <Label htmlFor={perPackId} className="text-xs">
            {t('admin.receiving.line.unitsPerPack')}
          </Label>
          <QuantityInput
            id={perPackId}
            value={unitsPerPack}
            onChange={(value) => onEdit('unitsPerPack', value)}
            disabled={disabled}
            invalid={Boolean(perPackIssue)}
            describedBy={perPackIssue ? `${perPackId}-error` : undefined}
            testId="pack-units-per-pack-input"
          />
          {perPackIssue ? (
            <p id={`${perPackId}-error`} className="text-destructive text-xs">
              {t(issueMessageKey(perPackIssue.code), {
                ...perPackIssue.meta,
                max: RECEIVING_LIMITS.maxUnitsPerPack,
              })}
            </p>
          ) : null}
        </div>
      </div>

      {total !== null ? (
        <div className="space-y-2">
          <p className="text-foreground text-sm" data-testid="pack-math">
            {/* Localized sentence: `<bdi>` (dir=auto) follows the text's own direction. */}
            <bdi>
              {t('admin.receiving.line.packMath', { packs: packsExpected, unitsPerPack, total })}
            </bdi>
          </p>
          <label
            htmlFor={confirmId}
            className={cn(
              'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm',
              confirmed
                ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/15'
                : 'border-input bg-card',
              disabled ? 'cursor-not-allowed opacity-60' : '',
            )}
          >
            <input
              id={confirmId}
              type="checkbox"
              className="border-input text-admin-lime focus:ring-admin-lime size-5 rounded"
              checked={confirmed}
              onChange={(event) => onConfirm(event.target.checked)}
              disabled={disabled}
              aria-invalid={confirmIssue ? true : undefined}
              aria-describedby={confirmIssue ? `${confirmId}-error` : undefined}
              data-testid="pack-confirm-checkbox"
            />
            <span className="text-foreground">{t('admin.receiving.line.confirmPack')}</span>
          </label>
          {confirmIssue ? (
            <p id={`${confirmId}-error`} className="text-destructive text-xs" role="status">
              {t(issueMessageKey(confirmIssue.code))}
            </p>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
